import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid-premium";

/**
 * Important context:
 * if 'include', ids are a set of selected rows
 * if 'exclude', ids are a set of ids that are not selected (assume everything else IS selected)
 * if nothing is selected -> 'include' with an empty set
 * if everything is selected -> 'exclude' with an empty set
 */

export const emptyRowSelectionModel = (): GridRowSelectionModel => ({
  type: "include",
  ids: new Set<GridRowId>(),
});

/**
 * True when any rows are selected.
 */
export const hasRowSelection = (model: GridRowSelectionModel): boolean =>
  model.type === "exclude" || model.ids.size > 0;

/**
 * Count of selected rows.
 * For `exclude` (select-all), pass `totalRowCount` so size is total − deselected.
 * Without a total, any exclude model is treated as non-empty (Infinity) so
 * `size === 0` checks stay correct for the select-all case.
 */
export const rowSelectionModelSize = (
  model: GridRowSelectionModel,
  totalRowCount?: number
): number => {
  if (model.type === "exclude") {
    if (totalRowCount !== undefined) {
      return Math.max(0, totalRowCount - model.ids.size);
    }
    return Number.POSITIVE_INFINITY;
  }
  return model.ids.size;
};

/**
 * Resolve selected row ids.
 * For `exclude` (select-all), `allIds` is required (e.g. loaded/selectable row
 * ids); selected = allIds minus the excluded set.
 */
export const rowSelectionModelToArray = (
  model: GridRowSelectionModel,
  allIds?: readonly GridRowId[]
): GridRowId[] => {
  if (model.type === "exclude") {
    if (!allIds) {
      throw new Error(
        "rowSelectionModelToArray: allIds is required when selection model type is exclude"
      );
    }
    return allIds.filter((id) => !model.ids.has(id));
  }
  return [...model.ids];
};

/**
 * Selected ids that are currently visible.
 * An include model otherwise keeps ids that have left the result set; those
 * must be dropped so an action cannot target a hidden row.
 */
export const rowSelectionModelToVisibleArray = (
  model: GridRowSelectionModel,
  visibleIds: readonly GridRowId[]
): GridRowId[] => {
  if (model.type === "exclude") {
    return visibleIds.filter((id) => !model.ids.has(id));
  }
  const visible = new Set(visibleIds);
  return [...model.ids].filter((id) => visible.has(id));
};

export const arrayToRowSelectionModel = (
  ids: GridRowId[]
): GridRowSelectionModel => ({
  type: "include",
  ids: new Set(ids),
});
